import * as React from 'react';
// The Marti launcher comes ONLY from @martletai/marti-sdk-react (renders "Marti"). The
// dual-brand build keeps export identifiers identical across brands, so the launcher + URL
// builder are its named exports here; the exact names are confirmed against the installed
// @martletai artifact at build. No cross-brand package is imported in this (Martlet) leg.
import { AnnaLauncher, buildAnnaUrl } from '@martletai/marti-sdk-react';
import { SDK_VERSION } from './sdk-version';
import { probeSdk, type WidenedUrlBuilder } from './sdk-capabilities';

const WIDENED_BUILD = buildAnnaUrl as unknown as WidenedUrlBuilder;

/** What the SERVER decided this viewer may do. The frontend renders ONLY this (server-side
 * gating — no client-side permission hook; capabilities come from /demo/whoami). */
interface WhoAmI {
  held?: boolean;
  authenticated?: boolean;
  user?: { id: string; name: string; tenantId: string };
  tier?: string;
  capabilities?: string[];
  canWrite?: boolean;
}

function useWhoAmI(): WhoAmI | null {
  const [who, setWho] = React.useState<WhoAmI | null>(null);
  React.useEffect(() => {
    fetch('/demo/whoami', { credentials: 'include', headers: { accept: 'application/json' } })
      .then((r) => r.json())
      .then(setWho)
      .catch(() => setWho({ authenticated: false }));
  }, []);
  return who;
}

export function App(): React.ReactElement {
  const who = useWhoAmI();
  const env: 'dev' | 'prod' = 'prod';
  const caps = React.useMemo(() => probeSdk(WIDENED_BUILD, env), [env]);

  if (who === null) return <Shell><p>Loading…</p></Shell>;

  if (who.held) {
    return (
      <Shell>
        <div className="notice">
          <strong>Awaiting login configuration.</strong> This Marti demo is held (fail-closed):
          the Keycloak front door is not configured yet. It lights up when Infra injects the
          OIDC values — no rebuild.
        </div>
      </Shell>
    );
  }

  if (!who.authenticated) {
    return (
      <Shell>
        <p>You are signed out. <a href="/login">Sign in with Martlet</a> to use the demo.</p>
      </Shell>
    );
  }

  const can = (c: string) => (who.capabilities ?? []).includes(c);

  return (
    <Shell>
      <dl className="facts">
        <dt>You</dt><dd>{who.user?.name} · tier <strong>{who.tier}</strong></dd>
        <dt>SDK</dt><dd><code>@martletai/marti-sdk-react@{SDK_VERSION}</code></dd>
        <dt>Launcher opens</dt><dd>{caps.launcherOrigin ?? `— (${caps.launcherError})`}</dd>
        <dt>SDK honours baseUrl</dt><dd>{caps.baseUrlOverride ? 'yes' : 'no'}</dd>
      </dl>

      {/* The real published launcher — renders "Marti", opens the deployed Marti with the
          viewer's tenant. Gated: only a tier the server granted launch may mount it. */}
      {can('marti:demo:launch') ? (
        <div className="mount">
          <AnnaLauncher src="marti-sdk-demo" tenant={who.user?.tenantId ?? ''} env={env} role="uat-tester" />
        </div>
      ) : (
        <p className="small">Your tier may not launch Marti.</p>
      )}

      {/* Settings — a WRITE surface, shown ONLY when the server says canWrite. */}
      <section>
        <h3>Settings</h3>
        {who.canWrite ? (
          <form onSubmit={(e) => e.preventDefault()}>
            <label>Target Marti env <select defaultValue={env}><option>dev</option><option>prod</option></select></label>
            <button type="submit">Save</button>
          </form>
        ) : (
          <p className="small">Read-only tier — settings are view-only.</p>
        )}
      </section>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }): React.ReactElement {
  // Minimal Martlet-branded chrome. The full @martletai/ui MartletShell + @martletai/design-tokens
  // are wired in the build phase (their exact API confirmed on install); this keeps the demo
  // buildable + brand-correct meanwhile. data-brand="martlet" is on <html>.
  return (
    <main style={{ fontFamily: 'system-ui, sans-serif', maxWidth: 720, margin: '0 auto', padding: 16 }}>
      <header><h1>MARTLET / Marti SDK Demo</h1></header>
      {children}
    </main>
  );
}
